import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { ShieldCheck, Mail, ArrowRight, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const DOMAIN = "@dhwaniris.com";

export default function Login() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState("email");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (user) return <Navigate to="/" replace />;
  const domainBad = email.includes("@") && !email.trim().toLowerCase().endsWith(DOMAIN);

  const send = async (e) => {
    e?.preventDefault();
    if (domainBad || !email) return setError(`Only ${DOMAIN} addresses can sign in.`);
    setBusy(true); setError("");
    try {
      await api.post("/auth/request-otp", { email });
      setStep("otp"); setCode("");
      toast.success(`Code sent to ${email}`);
    } catch (err) { setError(errorMessage(err)); }
    setBusy(false);
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/verify-otp", { email, code });
      localStorage.setItem("admin_token", data.token);
      setUser({ email: data.email }); nav("/");
    } catch (err) { setError(errorMessage(err)); }
    setBusy(false);
  };

  return (
    <div className="min-h-screen grid-bg flex items-center px-6 sm:px-16">
      <div className="w-full max-w-md rise">
        <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-indigo-400 mb-6">
          <ShieldCheck className="h-4 w-4" /> Restricted · Dhwani RIS
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight">Prime Rural docs Chatbot</h1>
        <p className="text-slate-400 mt-3 mb-10 text-sm sm:text-base">Monitor document syncs and the knowledge index behind the chatbot API. Access is limited to approved team members.</p>

        <div className="bg-[#0F172A]/90 backdrop-blur-xl border border-slate-700 rounded-xl p-6 sm:p-8">
          {step === "email" ? (
            <form onSubmit={send} className="space-y-4">
              <label className="font-mono text-xs uppercase tracking-wider text-slate-400">Work email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <Input data-testid="login-email-input" type="email" autoFocus required placeholder={`name${DOMAIN}`}
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  className="pl-9 h-11 bg-[#080C14] border-slate-700 text-slate-100 focus-visible:ring-indigo-500" />
              </div>
              {domainBad && <p data-testid="login-domain-error-message" className="text-amber-400 text-xs flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" />Only {DOMAIN} addresses are allowed.</p>}
              <Button data-testid="login-send-otp-button" disabled={busy || domainBad} type="submit"
                className="w-full h-11 bg-indigo-500 hover:bg-indigo-600 active:scale-[0.98] transition-transform">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Send login code <ArrowRight className="h-4 w-4 ml-1" /></>}
              </Button>
            </form>
          ) : (
            <form onSubmit={verify} className="space-y-5">
              <div>
                <p className="font-mono text-xs uppercase tracking-wider text-slate-400">Enter 6-digit code</p>
                <p className="text-sm text-slate-300 mt-1">Sent to <span className="text-indigo-300" data-testid="login-otp-email">{email}</span></p>
              </div>
              <InputOTP data-testid="login-otp-input" maxLength={6} value={code} onChange={setCode} autoFocus>
                <InputOTPGroup>
                  {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="h-12 w-12 text-lg font-mono border-slate-700 bg-[#080C14]" />)}
                </InputOTPGroup>
              </InputOTP>
              <Button data-testid="login-verify-otp-button" disabled={busy || code.length !== 6} type="submit"
                className="w-full h-11 bg-indigo-500 hover:bg-indigo-600 active:scale-[0.98] transition-transform">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & sign in"}
              </Button>
              <div className="flex justify-between text-xs">
                <button type="button" data-testid="login-change-email-button" onClick={() => { setStep("email"); setError(""); }} className="text-slate-400 hover:text-slate-200 transition-colors">Change email</button>
                <button type="button" data-testid="login-resend-otp-button" onClick={send} disabled={busy} className="text-indigo-400 hover:text-indigo-300 transition-colors">Resend code</button>
              </div>
            </form>
          )}
          {error && <p data-testid="login-error-message" className="mt-4 text-sm text-red-400">{error}</p>}
        </div>
      </div>
    </div>
  );
}
